'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Share2, X, Link as LinkIcon, Facebook, Mail, Check, QrCode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet/sheet';
import { cn } from '@/lib/utils';
import { XIcon, WhatsAppIcon, LinkedInIcon, TelegramIcon } from '@/components/icons/social';
import { useIsMobile } from '@/hooks/use-mobile';
import { QrCodeModal } from '@/components/qr-code-modal';
import { useTenantSlug } from '@/hooks/use-tenant-slug';
import { getTenantConfig } from '@/tenants/config';

export interface ShareDropdownProps {
  url: string;
  title: string;
  className?: string;
  /** When true, hides the "Share" label and shows only the icon */
  iconOnly?: boolean;
  /**
   * When true, styles the trigger for placement over dark imagery (page heroes)
   * regardless of the tenant's light/dark chrome.
   */
  onDark?: boolean;
  onShare?: (channel: string) => void;
}

interface ShareOption {
  id: string;
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  action: () => void;
}

export function ShareDropdown({
  url,
  title,
  className,
  iconOnly,
  onDark,
  onShare,
}: ShareDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isQrOpen, setIsQrOpen] = useState(false);
  const isMobile = useIsMobile();
  const tenantSlug = useTenantSlug();
  const tenantConfig = getTenantConfig(tenantSlug);
  const t = useTranslations('fundraiser.share');
  // Share blurb is customised per tenant via the campaign name (e.g. "United We
  // Ride") rather than being hardcoded to any one campaign.
  const shareMessage = t('shareMessage', { name: title, campaign: tenantConfig.displayName });
  // Light dashboard chrome (white/cream surface) flips the share button to a
  // tinted-dark-on-light style so it stays visible. Driven by tenant config so
  // any future light-theme tenant works automatically.
  const isLight = tenantConfig.donationTheme === 'light';
  const isDgd = tenantSlug === 'dgd';
  const triggerClass =
    onDark || !isLight
      ? 'text-white/80 hover:text-white hover:bg-white/10 border border-white/20'
      : isDgd
        ? 'text-dgd-charcoal/80 hover:text-dgd-charcoal hover:bg-dgd-charcoal/5 border border-dgd-charcoal/20'
        : 'text-stone-700 hover:text-stone-900 hover:bg-stone-100 border border-stone-300';

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      onShare?.('copy_link');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access was denied; no share occurred.
    }
  };

  const shareOptions: ShareOption[] = [
    {
      id: 'copy_link',
      name: t('copyLink'),
      icon: LinkIcon,
      color: 'bg-stone-700',
      action: handleCopyLink,
    },
    {
      id: 'whatsapp',
      name: 'WhatsApp',
      icon: WhatsAppIcon,
      color: 'bg-[#25D366]',
      action: () => {
        onShare?.('whatsapp');
        window.open(
          `https://wa.me/?text=${encodeURIComponent(`${shareMessage} ${url}`)}`,
          '_blank',
        );
        setIsOpen(false);
      },
    },
    {
      id: 'telegram',
      name: 'Telegram',
      icon: TelegramIcon,
      color: 'bg-[#229ED9]',
      action: () => {
        onShare?.('telegram');
        window.open(
          `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(shareMessage)}`,
          '_blank',
        );
        setIsOpen(false);
      },
    },
    {
      id: 'facebook',
      name: 'Facebook',
      icon: Facebook,
      color: 'bg-[#1877F2]',
      action: () => {
        onShare?.('facebook');
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
          '_blank',
          'width=600,height=400',
        );
        setIsOpen(false);
      },
    },
    {
      id: 'x',
      name: 'X',
      icon: XIcon,
      color: 'bg-black',
      action: () => {
        onShare?.('x');
        window.open(
          `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(shareMessage)}`,
          '_blank',
          'width=600,height=400',
        );
        setIsOpen(false);
      },
    },
    {
      id: 'linkedin',
      name: 'LinkedIn',
      icon: LinkedInIcon,
      color: 'bg-[#0A66C2]',
      action: () => {
        onShare?.('linkedin');
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
          '_blank',
          'width=600,height=400',
        );
        setIsOpen(false);
      },
    },
    {
      id: 'email',
      name: t('email'),
      icon: Mail,
      color: 'bg-stone-600',
      action: () => {
        onShare?.('email');
        window.location.href = `mailto:?subject=${encodeURIComponent(t('emailSubject'))}&body=${encodeURIComponent(`${t('emailBody', { name: title })}: ${url}`)}`;
        setIsOpen(false);
      },
    },
    {
      id: 'qr_code',
      name: t('qrCode'),
      icon: QrCode,
      color: 'bg-stone-700',
      action: () => {
        onShare?.('qr_code');
        setIsOpen(false);
        setIsQrOpen(true);
      },
    },
  ];

  return (
    <div className={cn('relative', className)}>
      <Button variant="ghost" onClick={() => setIsOpen(!isOpen)} className={triggerClass}>
        <Share2 className={cn('w-4 h-4', !iconOnly && 'mr-2')} />
        {!iconOnly && t('share')}
      </Button>

      {/* Desktop Dropdown */}
      {!isMobile && isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div
            className={cn(
              'absolute right-0 top-full mt-2 w-48 backdrop-blur-md border rounded-xl shadow-xl z-50 overflow-hidden',
              isLight ? 'bg-white/95 border-stone-300' : 'bg-stone-900/95 border-stone-700',
            )}
          >
            <div
              className={cn(
                'flex items-center justify-between px-3 py-2 border-b',
                isLight ? 'border-stone-300' : 'border-stone-700',
              )}
            >
              <span
                className={cn('text-sm font-medium', isLight ? 'text-stone-900' : 'text-white')}
              >
                {t('share')}
              </span>
              <button
                onClick={() => setIsOpen(false)}
                className={cn(
                  'p-1 rounded-full transition-colors',
                  isLight ? 'hover:bg-stone-200' : 'hover:bg-stone-700',
                )}
              >
                <X className={cn('w-4 h-4', isLight ? 'text-stone-500' : 'text-stone-400')} />
              </button>
            </div>
            <div className="py-1">
              {shareOptions.map((option) => {
                const IconComponent = option.icon;
                return (
                  <button
                    key={option.id}
                    onClick={option.action}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors',
                      isLight
                        ? 'text-stone-600 hover:bg-stone-100 hover:text-stone-900'
                        : 'text-stone-300 hover:bg-stone-800 hover:text-white',
                    )}
                  >
                    {option.id === 'copy_link' && copied ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <IconComponent className="w-4 h-4" />
                    )}
                    <span>{option.id === 'copy_link' && copied ? t('copied') : option.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Mobile Sheet */}
      {isMobile && (
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetContent
            side="bottom"
            className={cn(
              'rounded-t-2xl',
              isLight ? 'bg-white border-stone-200' : 'bg-stone-950 border-stone-800',
            )}
          >
            <SheetHeader className="pb-4">
              <SheetTitle className={cn('text-center', isLight ? 'text-stone-900' : 'text-white')}>
                {t('share')}
              </SheetTitle>
            </SheetHeader>
            <div className="grid grid-cols-3 gap-4 pb-6">
              {shareOptions.map((option) => {
                const IconComponent = option.icon;
                return (
                  <button
                    key={option.id}
                    onClick={option.action}
                    className="flex flex-col items-center gap-2"
                  >
                    <div
                      className={`w-14 h-14 rounded-full ${option.color} flex items-center justify-center`}
                    >
                      {option.id === 'copy_link' && copied ? (
                        <Check className="w-6 h-6 text-white" />
                      ) : (
                        <span className="text-white">
                          <IconComponent className="w-6 h-6" />
                        </span>
                      )}
                    </div>
                    <span className={cn('text-xs', isLight ? 'text-stone-500' : 'text-stone-400')}>
                      {option.id === 'copy_link' && copied ? t('copied') : option.name}
                    </span>
                  </button>
                );
              })}
            </div>
            <Button
              variant="ghost"
              onClick={() => setIsOpen(false)}
              className={cn(
                'w-full pb-safe',
                isLight
                  ? 'text-stone-500 hover:text-stone-900 hover:bg-stone-100'
                  : 'text-stone-400 hover:text-white hover:bg-stone-800',
              )}
            >
              {t('cancel')}
            </Button>
          </SheetContent>
        </Sheet>
      )}

      <QrCodeModal open={isQrOpen} onOpenChange={setIsQrOpen} url={url} title={title} />
    </div>
  );
}
